<template>
  <div class="client-transaction-card">
    <div class="card text-center">
      <div v-if="!embed">
        <BButton class="client-transaction-card__share" variant="dark" @click="toggleSharing">
          <FontAwesomeIcon icon="share-alt" size="2x" />
          <span class="d-block">
            {{ $t('global.share') }}
          </span>
        </BButton>
        <Portal to="overlay">
          <ContentOverlay @close="toggleSharing" v-if="showSharing" class="client-transaction-card__overlay">
            <EmbedForm no-title :url="embedUrl" :width="'100%'" :height="500"></EmbedForm>
          </ContentOverlay>
        </Portal>
      </div>
      <div>
        <h5>
          $ {{ amount }} ({{ $tc('clientTransactionCard.transaction', transactionsCount, { count: transactionsCount }) }})
        </h5>
        <div v-if="dates">
          <span v-if="dates.type === 'single'">
            {{ dates.dates[0] | moment }}
          </span>
          <span v-else-if="dates.type === 'to'">
            {{ $t('clientTransactionCard.from') }} {{ dates.dates[0] | moment }} {{ $t('clientTransactionCard.to') }} {{ dates.dates[1] | moment }}
          </span>
          <span v-else-if="dates.type === 'and'">
            {{ $t('clientTransactionCard.on') }} {{ dates.dates[0] | moment }} {{ $t('clientTransactionCard.and') }} {{ dates.dates[1] | moment }}
          </span>
        </div>
      </div>
      <BRow class="client-transaction-card__journey mt-4 mx-3 d-block d-sm-flex">
        <BCol>
          <div class="text-orange">
            {{ $t('clientTransactionCard.originator') }}
          </div>
          <div class="font-weight-bold">
            {{ originator }}
            <span v-if="originatorCountry">
              ({{ originatorCountry }})
            </span>
          </div>
        </BCol>
        <BCol cols="12" sm="6" lg="8" class="m-auto">
          <div v-if="transaction.sentence">
            {{ this.transaction.sentence }}
          </div>
          <div class="d-none d-md-block" v-else>
            {{ $t('clientTransactionCard.description', { count: this.transaction['amount_(in_sentence)'] }) }} {{ $tc('clientTransactionCard.continent', this.numberOfContinents, { count: this.numberOfContinents }) }}, {{ $tc('clientTransactionCard.country', this.numberOfCountries, { count: this.numberOfCountries }) }} {{ $t('global.and') }} {{ $tc('clientTransactionCard.bank', this.numberOfBanks, { count: this.numberOfBanks }) }}
          </div>
          <BRow class="no-gutters">
            <BCol class="d-flex justify-content-end align-items-center">
              <div class="client-transaction-card__journey__circle"></div>
            </BCol>
            <BCol cols="10" sm="8" lg="10" class="m-auto">
              <div class="client-transaction-card__journey__horizontal-bar mt-2 mb-2"></div>
            </BCol>
            <BCol class="d-flex justify-content-start align-items-center">
              <div class="client-transaction-card__journey__circle"></div>
            </BCol>
          </BRow>
          <div class="text-muted d-none d-md-block">
            {{ $t('clientTransactionCard.followTheJourney') }}
          </div>
        </BCol>
        <BCol>
          <div class="text-orange">
            {{ $t('clientTransactionCard.beneficiary') }}
          </div>
          <div class="font-weight-bold">
            {{ beneficiary }}
            <span v-if="beneficiaryCountry">
              ({{ beneficiaryCountry }})
            </span>
          </div>
        </BCol>
      </BRow>
      <BRow class="d-md-none">
        <BCol>
          <div class="mt-3 mb-5 mx-5">
            {{ $t('clientTransactionCard.description', { count: this.transaction['amount_(in_sentence)'] }) }} {{ $tc('clientTransactionCard.continent', this.numberOfContinents, { count: this.numberOfContinents }) }}, {{ $tc('clientTransactionCard.country', this.numberOfCountries, { count: this.numberOfCountries }) }} {{ $t('global.and') }} {{ $tc('clientTransactionCard.bank', this.numberOfBanks, { count: this.numberOfBanks }) }}
          </div>
        </BCol>
      </BRow>
      <div class="client-transaction-card__journey__map">
        <div class="client-transaction-card__journey__map__steps px-1 my-2">
          <div v-for="(country, index) in selectedCountriesName" :key="index" v-show="show[index]">
            <div class="client-transaction-card__journey__map__steps__step px-2 py-1 mb-2" :id="`step-${index}`" @click="index === 0 ? startAnimation() : null">
              <span class="client-transaction-card__journey__map__steps__step__title text-white px-2 py-1">
                <span class="d-md-none">
                  {{ country }}
                </span>
                <span class="d-none d-md-inline-block">
                  {{ index + 1 }}
                </span>
              </span>
              <div v-html="snarkdown(selectedTexts[index])" v-if="selectedTexts[index]"></div>
            </div>
            <svg width="20px" height="60px" v-if="index !== selectedCountriesName.length - 1" class="d-md-none">
              <path d="M 8 0 8 40" stroke="black" stroke-width="2" style="stroke-dasharray: 5,3;"></path>
              <path d="M 0 40 16 40 8 56 0 40"></path>
            </svg>
          </div>
        </div>
        <BRow class="client-transaction-card__journey__map__svg d-none d-md-block">
          <BCol>
            <div>
              <svg class="map" ref="map" width="100%">
                <g class="paths"></g>
                <g class="lines"></g>
                <g class="circles"></g>
                <g class="legend">
                  <circle id="legend-originator" cx="50" cy="560" r="12" fill="rgb(128, 128, 128)"></circle>
                  <text x="65" y="565">Originator</text>
                  <circle id="legend-beneficiary" cx="170" cy="560" r="12" fill="rgb(0, 0, 0)"></circle>
                  <text x="185" y="565">Beneficiary</text>
                </g>
                <g class="markers">
                  <marker id="triangle" refX="12" refY="4" markerWidth="8" markerHeight="8" orient="auto">
                    <path d="M 0 0 8 4 0 8 0 0"></path>
                  </marker>
                </g>
              </svg>
            </div>
          </BCol>
        </BRow>
      </div>
    </div>
  </div>
</template>

<script>
  import { compact, fill, find, get, map, max, split, uniqBy } from 'lodash'
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
  import * as d3 from 'd3'
  import { geoNaturalEarth1 } from 'd3-geo'
  import moment from 'moment'
  import snarkdown from 'snarkdown'

  import ContentOverlay from '@/components/ContentOverlay'
  import transactions from '@/data/transactions.json'
  import transactions_other from '@/data/transactions_other.json'
  import worldmap from '@/data/worldmap.json'
  import utils from '@/mixins/utils'
  import { getClientId } from '@/utils/client'

  export default {
    name: 'ClientTransactionCard',
    components: {
      ContentOverlay,
      FontAwesomeIcon
    },
    mixins: [utils],
    props: {
      embed: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        colorBeneficiary: 'black',
        colorOriginator: 'gray',
        isAnimationPlayed: false,
        showSharing: false,
        show: []
      }
    },
    computed: {
      clientId () {
        return getClientId(this.$store.state.clients.ordered, this.$route.params.id)
      },
      height () {
        return 600 * this.width / 1078
      },
      width () {
        return this.$refs.map.clientWidth
      },
      scale () {
        if (this.width < 768) {
          return 120
        } else if (this.width < 900) {
          return 180
        } else {
          return 220
        }
      },
      map () {
        return d3.select('.map')
          .attr('width', this.width)
          .attr('height', this.height)
          .classed('svg-content', true)
      },
      projection() {
        return geoNaturalEarth1()
            .translate([this.width / 2.2, this.height / 1.8])
            .scale(this.scale)
      },
      path () {
        return d3.geoPath(this.projection)
      },
      selectedCountriesName () {
        return compact([
          this.transaction.originatorbank_country,
          this.transaction.beneficiarybank1_country,
          this.transaction.beneficiarybank2_country,
          this.transaction.beneficiarybank3_country,
          this.transaction.beneficiarybank4_country
        ])
      },
      selectedCountries () {
        return map(this.selectedCountriesName, name => find(worldmap.features, country => country.properties.name_sort === name || country.properties.name === name))
      },
      selectedBanksGPS () {
        const urls = compact([
            this.transaction.originatorbank_gps,
            this.transaction.beneficiarybank1_gps,
            this.transaction.beneficiarybank2_gps,
            this.transaction.beneficiarybank3_gps,
            this.transaction.beneficiarybank4_gps,
        ])
        return map(urls, url => this.extractGpsFromUrl(url))
      },
      selectedBanksGPSArray () {
        return compact(map(this.selectedBanksGPS,
            (country, index) => this.selectedBanksGPS[index + 1] ? [country, this.selectedBanksGPS[index + 1]] : null))
      },
      selectedTexts () {
        return compact([
          this.transaction.originatorbank_text,
          this.transaction.beneficiarybank1_text,
          this.transaction.beneficiarybank2_text,
          this.transaction.beneficiarybank3_text,
          this.transaction.beneficiarybank4_text
        ])
      },
      transaction () {
        const transactionsList = this.clientId === 'global-overview' ? transactions_other.clients : transactions.clients
        return find(transactionsList, { 'profile_#': this.clientId })
      },
      amount () {
        const amount = get(this.transaction, 'amount_(top)', null)
        return amount === null ? this.$t('global.unknown') : new Intl.NumberFormat(this.$i18n.locale).format(amount)
      },
      transactionsCount () {
        return get(this.transaction, 'number_of_transactions', 0)
      },
      dates () {
        const date = get(this.transaction, 'transaction_date_(reference)', null)
        if (!date) return null
        const dateRange = date.match(/(.*) to (.*)/)
        const dateRange2 = date.match(/(.*) and (.*)/)
        if (dateRange) {
          return { type: 'to', dates: [dateRange[1], dateRange[2]] }
        } else if (dateRange2) {
          return { type: 'and', dates: [dateRange2[1], dateRange2[2]] }
        } else {
          return { type: 'single', dates: [date] }
        }
      },
      originator () {
        return get(this.transaction, 'originator_name', this.$t('global.unknown'))
      },
      originatorCountry () {
        return get(this.transaction, 'originator_country', '')
      },
      beneficiary () {
        return get(this.transaction, 'finalbeneficiary_name', this.$t('global.unknown'))
      },
      beneficiaryCountry () {
        return get(this.transaction, 'finalbeneficiary_country', '')
      },
      numberOfContinents () {
        return uniqBy(this.selectedCountries, 'properties.continent').length
      },
      numberOfCountries () {
        return uniqBy(this.selectedCountries, 'properties.name').length
      },
      numberOfBanks () {
        return compact([
          this.transaction.originatorbank_name,
          this.transaction.beneficiarybank1_name,
          this.transaction.beneficiarybank2_name,
          this.transaction.beneficiarybank3_name,
          this.transaction.beneficiarybank4_name
        ]).length
      },
      embedUrl () {
        return this.getFullUrl(this.$router.resolve({ name: 'client.transaction.embed' }).href)
      }
    },
    filters: {
      moment: function (date) {
        return moment(date).format('LL')
      }
    },
    watch: {
      clientId () {
        this.displayPaths()
        this.init()
        d3.select('body').on('keypress', () => {
          if(d3.event.key === ' ') {
            this.startAnimation()
          }
        })
      }
    },
    methods: {
      extractGpsFromUrl (url) {
        const [lat, long] = split(get(split(url, '@', 2), 1), ',', 2)
        return [long, lat]
      },
      displayPaths () {
        this.map.select('.paths')
          .selectAll('path')
          .data(worldmap.features)
          .enter()
          .append('path')
          .attr('d', this.path)
          .attr('fill', '#F2F2F2')
          .attr('stroke', '#CCC')
      },
      isAlmostEqual (from, to) {
        const x = Math.abs(from[0] - to[0])
        const y = Math.abs(from[1] - to[1])
        return (x < 1) && (y < 1)
      },
      displayLine (index = 0) {
        const [from, to] = this.selectedBanksGPSArray[index]
        if (this.isAlmostEqual(from, to)) {
          const cx = this.projection(from)[0]
          const cy = this.projection(from)[1]
          const r = 13
          this.map.select('.lines')
            .append('path')
            .attr('class', 'line')
            .attr('d', `M ${cx}, ${cy} a ${r},${r} 0 1,0 -${r * 2},0 a ${r},${r} 0 1,0 ${r * 2},0 z`)
            .attr('stroke', 'black')
            .attr('fill', 'none')
            .attr('stroke-width', 2)
            .style('stroke-dasharray', ('5, 2'))
            .style('opacity', 0)
            .transition()
            .duration(1000)
            .style('opacity', 1)
          return this.map.select('.lines')
            .append('path')
            .attr('class', 'line')
            .attr('d', 'M 0 0 10 5 0 10 0 0')
            .attr('transform', `translate(${cx - 13}, ${cy + 9}) rotate(-30)`)
            .style('opacity', 0)
            .transition()
            .duration(1000)
            .style('opacity', 1)
            .end()
        } else {
          return this.map.select('.lines')
            .append('line')
            .attr('class', 'line')
            .attr('x1', this.projection(from)[0])
            .attr('y1', this.projection(from)[1])
            .attr('x2', this.projection(to)[0])
            .attr('y2', this.projection(to)[1])
            .attr('stroke', 'black')
            .attr('stroke-width', 2)
            .style('stroke-dasharray', ('5, 2'))
            .attr('marker-end', 'url(#triangle)')
            .style('opacity', 0)
            .transition()
            .duration(1000)
            .style('opacity', 1)
            .end()
        }
      },
      displayHalfCircleBeneficiary (gps, index = 'beneficiary') {
        const color = this.colorBeneficiary
        return this.map.select('.circles')
            .append('path')
            .attr('id', `circle-${index}`)
            .attr('d', 'M 0 -12 A 12 12 0 0 1 0 12')
            .attr('transform', `translate(${this.projection(gps)})`)
            .attr('fill', 'transparent')
            .style('opacity', 1)
            .transition()
            .duration(1000)
            .attr('fill', color)
            .end()
      },
      displayCircle (gps, index = null) {
        let color = '#EDAC3A'
        switch (index) {
          case 'originator' :
            color = this.colorOriginator
            break
          case 'beneficiary' :
            color = this.colorBeneficiary
            break
        }
        if (compact(gps).length === 2) {
          if (color === '#EDAC3A') {
            this.map.select('.circles')
              .append('circle')
              .attr('transform', `translate(${this.projection(gps)})`)
              .attr('r', 9)
              .attr('fill', 'transparent')
              .style('opacity', 1)
              .transition()
              .duration(1000)
              .attr('fill', 'white')
              .end()
          }
          return this.map.select('.circles')
            .append('circle')
            .attr('id', `circle-${index}`)
            .attr('transform', `translate(${this.projection(gps)})`)
            .attr('r', (index === parseInt(index)) ? 9 : 12)
            .attr('fill', 'transparent')
            .style('opacity', (index === parseInt(index)) ? 0.5 : 1)
            .on('click', () => {
              if(index === 0) {
                this.startAnimation()
              }
            })
            .on('mouseover', function () {
              if (index === parseInt(index)) {
                d3.select(`#step-${index}`).style('opacity', 1)
                d3.select(this).style('opacity', 1)
              }
            })
            .on('mouseout', function () {
              if (index === parseInt(index)) {
                d3.select(`#step-${index}`).style('opacity', 0.5)
                d3.select(this).style('opacity', 0.5)
              }
            })
            .transition()
            .duration(1000)
            .attr('fill', color)
            .end()
        }
      },
      displayAllTexts () {
        this.$set(this, 'show', fill(Array(this.selectedTexts.length), true))
      },
      displayText (index = 0) {
        this.$set(this.show, index, true)
        d3.select(`#step-${index}`)
          .on('mouseover', function () {
            const id = d3.select(this).attr('id').replace('step-', '#circle-')
            d3.select(id).style('opacity', 1)
            d3.select(this).style('opacity', 1)
          })
          .on('mouseout', function () {
            const id = d3.select(this).attr('id').replace('step-', '#circle-')
            d3.select(id).style('opacity', 0.5)
            d3.select(this).style('opacity', 0.5)
          })
        this.$nextTick(() => {
          const marginBottom = 10
          const stepsHeight = document.getElementsByClassName('client-transaction-card__journey__map__steps')[0].clientHeight
          const svgHeight = document.getElementsByClassName('client-transaction-card__journey__map__svg')[0].clientHeight
          const height = max([stepsHeight, svgHeight]) + marginBottom
          document.getElementsByClassName('client-transaction-card__journey__map__svg')[0].style.height = `${height}px`
        })
        return null
      },
      displayStep (index = 0) {
        const promiseLine = this.displayLine(index)
        const promiseCircle = this.displayCircle(this.selectedBanksGPS[index + 1], index + 1)
        const promiseText = this.displayText(index + 1)
        return Promise.all([promiseLine, promiseCircle, promiseText])
      },
      async startAnimation () {
        if(!this.isAnimationPlayed) {
          this.$set(this, 'isAnimationPlayed', true)
          for(let i = 0; i < this.selectedCountriesName.length - 1; i++) {
            await this.displayStep(parseInt(i))
          }
        }
      },
      init () {
        d3.selectAll('.circles circle').remove()
        d3.selectAll('.line').remove()
        this.$set(this, 'isAnimationPlayed', false)
        this.$set(this, 'show', [])
        const gpsOriginator = this.extractGpsFromUrl(this.transaction.originator_gps)
        this.displayCircle(gpsOriginator, 'originator')
        const gpsFinaleBeneficiary = this.extractGpsFromUrl(this.transaction.finalbeneficiary_gps)
        if (this.isAlmostEqual(gpsOriginator, gpsFinaleBeneficiary)) {
          this.displayHalfCircleBeneficiary(gpsOriginator, 'beneficiary')
        } else {
          this.displayCircle(gpsFinaleBeneficiary, 'beneficiary')
        }
        this.displayCircle(this.selectedBanksGPS[0], 0)
        if (window.innerWidth < 768) {
          this.displayAllTexts()
        } else {
          this.displayText(0)
        }
      },
      toggleSharing () {
        this.$set(this, 'showSharing', !this.showSharing)
      },
      snarkdown
    },
    beforeRouteUpdate (to, from, next) {
      this.$set(this, 'clientId', to.params.id)
      this.init()
      next()
    },
    mounted () {
      this.displayPaths()
      this.init()
      d3.select('body').on('keypress', () => {
        if(d3.event.key === ' ') {
          this.startAnimation()
        }
      })
    }
  }
</script>

<style lang="scss">
  .text-orange {
    color: var(--app-yellow);
  }

  .client-transaction-card {
    padding: 19px 0;

    .card {
      padding-top: 4rem;
    }

    &__share {
      background: black;
      border: 0;
      border-radius: 4px 0 0 0;
      font-size: 10px;
      font-weight: bold;
      left: 4px;
      padding: 4px;
      position: absolute;
      text-transform: uppercase;
      top: 4px;
    }

    &__overlay {
      max-width: 1200px !important;

      .col-7 {
        flex: 0 0 75%;
        max-width: 1200px;
      }
    }

    &__journey {
      &__circle {
        background-color: transparent;
        border: 2px solid var(--app-yellow);
        border-radius: 50%;
        height: 15px;
        -moz-border-radius:50%;
        -webkit-border-radius:50%;
        width: 15px;
      }

      &__horizontal-bar {
        border: 1px solid var(--app-yellow);
      }

      &__map {
        position: relative;

        &__steps {
          &__step {
            background-color: var(--app-yellow);
            font-size: 13px;
            height: auto;
            opacity: 1;
            padding: 5px;

            &__title {
              background-color: black;
              color: white;
              margin-top: 7px;
              padding: 3px 10px;
            }
          }
        }
      }
    }
  }

  @media (min-width: 768px) {
    .client-transaction-card {
      .card {
        padding-top: 3rem;
      }

      &__journey__map__steps {
        position: absolute;
        right: 0;
        width: 25%;
        z-index: 1;

        &__step {
          opacity: 0.5;

          &#step-0 {
            cursor: pointer;
          }
        }
      }
    }
  }
</style>
